/**
 * @file Renders the action that starts or restarts the authenticator setup.
 */
import React, { useState } from "react";
import { Button, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";

import { useAppContext } from "@components/AppContext";
import type { AppContext } from "@components/AppContext/types";
import { startTotpSetupOperation } from "@api/services/identity";
import type { StartTotpSetupOutput } from "@api/types";

const LABEL_START = "Start authenticator setup";
const LABEL_RESET = "Restart setup";
const LABEL_SETUP_INSTRUCTION = "Start authenticator setup, scan the QR code with your authenticator app (or add the key manually), then enter the six-digit code.";

type Props = {
  hasSetupData: boolean;
  getAccessToken: () => Promise<string>;
  onSetupData: (secretCode: string, setupUri: string) => void;
}

/**
 * Renders the start or restart button for the authenticator setup.
 */
const StartTotpSetup = ({
  hasSetupData,
  getAccessToken,
  onSetupData,
}: Props) => {
  const { authenticatedRequest: request } = useAppContext() as AppContext;
  const [ isLoading, setIsLoading ] = useState(false);

  /**
   * Requests the TOTP secret and setup URI and passes them to the caller.
   */
  const startSetup = async () => {
    setIsLoading(true);

    try {
      const accessToken = await getAccessToken();
      const { data } = await request<StartTotpSetupOutput>(startTotpSetupOperation, {
        mutation: { accessToken },
      });

      onSetupData(data.secretCode, data.setupUri);
    } finally {
      setIsLoading(false);
    }
  };

  if (hasSetupData) {
    return (
      <>
        <Typography.Paragraph>
          {LABEL_SETUP_INSTRUCTION}
        </Typography.Paragraph>

        <Button
          icon={<ReloadOutlined />}
          onClick={startSetup}
          loading={isLoading}
        >
          {LABEL_RESET}
        </Button>
      </>
    );
  }

  return (
    <>
      <Typography.Paragraph>
        {LABEL_SETUP_INSTRUCTION}
      </Typography.Paragraph>

      <Button
        size="large"
        type="primary"
        onClick={startSetup}
        loading={isLoading}
      >
        {LABEL_START}
      </Button>
    </>
  );
};

export default StartTotpSetup;
